/**
 * §11.6 Platform Availability — SLA-flavoured fact grid.
 *
 * Six honest facts covering uptime target, RPO, RTO, backups, DR and
 * where the formal SLA lives (MSA / Enterprise).
 */

import React from 'react'
import clsx from 'clsx'
import {
  Gauge, Clock, RotateCcw, Archive, RefreshCw, FileSignature, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { AVAILABILITY_FACTS } from '../data'
import { TONE } from '../_shared'

// Because these fact cards are value-forward (big value / label + body),
// we don't reuse FeatureGrid.  We use a bespoke card layout so the
// big "99.9% monthly" number can breathe.
export default function PlatformAvailability() {
  return (
    <section id="availability" className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow tone="green">Availability</Eyebrow>
          <SectionHeading align="center"
            title={<>Uptime,{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">recovery and resilience.</span></>}
            subtitle="Concrete numbers rather than adjectives.  Formal service-credit remedies live in the MSA for Enterprise-tier customers."
          />
        </div>

        <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {AVAILABILITY_FACTS.map((f) => {
            const tone = TONE[f.tone] || TONE.orange
            return (
              <div key={f.id} className={clsx(
                'relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur-xl overflow-hidden',
                'transition-all hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[1px]',
              )}>
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />
                <div className="relative flex flex-col gap-3">
                  <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: tone.hex }}>
                    {f.label}
                  </div>
                  <div className="text-2xl md:text-3xl font-semibold text-white tracking-tight">
                    {f.value}
                  </div>
                  <div className="text-sm text-zinc-400 leading-relaxed">{f.body}</div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Caveat strip — honest about what the SLA is / isn't */}
        <div className="rounded-2xl border border-amber-400/20 bg-amber-400/[0.03] p-4 md:p-5 text-sm text-amber-100/80 leading-relaxed flex items-start gap-3">
          <FileSignature className="h-4 w-4 mt-0.5 flex-none text-amber-300" />
          <div>
            <span className="font-semibold text-amber-200">A note on SLAs.</span>{' '}
            The uptime target and remedies above are indicative of the current production posture.  The formal, contractual SLA — including service-credit calculations, exclusions and reporting cadence — is provided in the Master Services Agreement for Professional and Enterprise licences.
          </div>
        </div>
      </Container>
    </section>
  )
}
